/* =========================================================================
   Checkout, for a shopper signed in with their phone

   The phone sign-in on this theme (assets/login-sheet.js, _otp-backend) is
   the shop's own session, not a Shopify one. The store is on Shopify's new
   customer accounts without Shopify Plus, and on that setup nothing outside
   Shopify's own login can sign a customer into checkout (Plus can, by
   connecting this login as an identity provider). So checkout used to open
   as a stranger: empty contact, "Sign in" in the corner.

   What this file does instead: when the shopper is signed in here, every
   way into checkout carries what we already know about them, so checkout
   opens filled in and the order lands on their own customer record —

     Contact          their email if it is a real one, otherwise the mobile
                      number they signed in with
     First/last name  from their account
     Phone            the number they signed in with
     City, PIN code   from the delivery location picked in the header
                      (only a PIN code the shopper chose, not a guess from
                      their network)

   The three ways in: the cart drawer's Checkout link, the cart page's
   Checkout button and Buy now on a product page. Guests are untouched, and
   nothing is sent anywhere but Shopify's own checkout on this domain.
   ========================================================================= */
(function () {
  'use strict';
  if (window.VroCheckout) return;

  var SESSION_KEY = 'vro_session';            /* assets/login-sheet.js */
  var LOCATION_KEY = 'vro_detected_location'; /* assets/custom.js, location picker */
  var ADDRESS_KEY = 'vro_saved_address';      /* fetched below, this tab only */

  function read(key) {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }

  /* The address the shop already has on file, fetched once per tab.

     Checkout would offer this itself to anyone Shopify recognises, but a
     phone sign-in makes no Shopify session — so a customer whose address
     has been on file for a year still meets an empty form. The app can
     read the record even though checkout can't.

     Held in sessionStorage rather than fetched at click time: a request
     between tapping Checkout and arriving there is a delay on the one
     screen that should never have one. Whatever is cached when the tap
     comes is what goes; if the fetch hasn't landed, checkout opens as it
     does today and the shopper types their address, which is what they
     were doing anyway. */
  function savedAddress() {
    try { return JSON.parse(sessionStorage.getItem(ADDRESS_KEY) || 'null'); } catch (e) { return null; }
  }

  function loadSavedAddress() {
    if (!session() || savedAddress() || !window.VroCircleApi) return;
    window.VroCircleApi.api('/me/address').then(function (d) {
      if (d && d.address) {
        try { sessionStorage.setItem(ADDRESS_KEY, JSON.stringify(d.address)); } catch (e) {}
      }
    }).catch(function () { /* no address, or offline — checkout asks, as before */ });
  }

  /* The same check the login sheet makes: a stored token that has not run
     out. The backend re-checks it wherever it matters; this only decides
     whether to fill checkout in. */
  function session() {
    var s = read(SESSION_KEY);
    if (!s || !s.token || !s.customer) return null;
    try {
      var part = String(s.token).split('.')[1];
      if (part) {
        var payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.exp && Date.now() >= payload.exp * 1000) return null;
      }
    } catch (e) { return null; }
    return s;
  }

  /* +91XXXXXXXXXX, or '' when it cannot be read as a number. */
  function e164(v) {
    var d = String(v || '').replace(/[^\d+]/g, '');
    if (!d) return '';
    if (d.charAt(0) !== '+') {
      if (d.length === 10) d = '+91' + d;
      else if (d.length === 12 && d.indexOf('91') === 0) d = '+' + d;
      else return '';
    }
    return /^\+\d{8,15}$/.test(d) ? d : '';
  }

  /* A phone-only sign-up is given a stand-in address nobody reads
     (<number>@otp.yourvro.com, see _otp-backend/lib/shopify.js). Order
     emails sent there would be lost, so it never goes into checkout. */
  function realEmail(v) {
    v = String(v || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return '';
    if (/^\+?\d{8,15}@otp\./i.test(v) || /@otp\.example\.com$/i.test(v)) return '';
    return v;
  }

  /* Razorpay Magic Checkout owns checkout once it is switched on for the
     cart (Razorpay dashboard -> Checkout Config). Stepping in here would
     send the shopper straight to Shopify's checkout and skip it, so this
     file stands aside. Magic Checkout asks for the phone number itself. */
  function magicOwnsCheckout() {
    var el = document.getElementById('RazorpayMagicCheckoutConfig');
    if (!el) return false;
    try {
      var cfg = JSON.parse(el.textContent || '{}');
      return !!(cfg.config && cfg.config.merchant_key);
    } catch (e) { return false; }
  }

  /* The query string checkout reads (Shopify's documented checkout[...]
     prefill parameters), or '' for a guest. */
  function params() {
    var s = session();
    if (!s) return '';
    var c = s.customer || {};
    var out = [];
    function add(key, value) {
      value = String(value == null ? '' : value).trim();
      if (value) out.push(key + '=' + encodeURIComponent(value));
    }

    var phone = e164(s.loginPhone) || e164(c.phone);
    var email = realEmail(c.email);
    if (email) add('checkout[email]', email);
    else if (phone) add('checkout[phone]', phone);

    /* The saved address wins where it has an answer: it is a street the
       shopper wrote down, not a city inferred from where they are now. */
    var saved = savedAddress();

    add('checkout[shipping_address][first_name]', (saved && saved.first_name) || c.firstName);
    add('checkout[shipping_address][last_name]', (saved && saved.last_name) || c.lastName);
    add('checkout[shipping_address][phone]', (saved && saved.phone) || phone);

    if (saved && saved.address1) {
      add('checkout[shipping_address][address1]', saved.address1);
      add('checkout[shipping_address][address2]', saved.address2);
      add('checkout[shipping_address][city]', saved.city);
      add('checkout[shipping_address][province]', saved.province);
      add('checkout[shipping_address][zip]', saved.zip);
      add('checkout[shipping_address][country]', saved.country || 'India');
      return out.join('&');
    }

    /* Nothing on file, so the delivery location the shopper picked in the
       header is the best guess there is — the street line stays empty and
       they fill it in, as they do today. Only a PIN they chose: one guessed
       from their network would put a stranger's city on their order. */
    var loc = read(LOCATION_KEY);
    if (loc && !loc.approximate && /^\d{6}$/.test(String(loc.pincode || ''))) {
      add('checkout[shipping_address][country]', 'India');
      add('checkout[shipping_address][city]', loc.city);
      add('checkout[shipping_address][zip]', loc.pincode);
    }
    return out.join('&');
  }

  /* '/checkout' with the shopper's details on it. */
  function url(base) {
    base = base || '/checkout';
    var p = params();
    if (!p) return base;
    return base + (base.indexOf('?') > -1 ? '&' : '?') + p;
  }

  function isCheckoutPath(href) {
    try {
      var u = new URL(href, window.location.href);
      return u.origin === window.location.origin && /^\/checkout\/?$/.test(u.pathname);
    } catch (e) { return false; }
  }

  /* ---------------------------------------------------------------------
     Whose order it is.

     Shopify files an order under whoever the contact typed at checkout
     belongs to. Signed in here with a phone, then checking out with an
     email (or with a contact the cart remembered from before), the order
     went to a different customer record and never showed in the profile.

     So the cart carries a signed note of the signed-in account
     (_vro_account, from the OTP backend). It travels onto the order, and
     the backend then lists that order under this account whatever contact
     was used. Set whenever there is a cart and a session, checked again
     just before leaving for checkout, and removed on sign-out.
     --------------------------------------------------------------------- */
  var LINK_ATTR = '_vro_account';
  var LINK_CACHE = 'vro_cart_link';
  var LINK_MARK = 'vro_cart_linked';

  function apiBase() {
    var cfg = window.VroPersonalizationConfig;
    var base = (cfg && cfg.apiBase) || '';
    if (!base) {
      var sheet = document.getElementById('loginSheet');
      base = (sheet && sheet.getAttribute('data-otp-api-base')) || '';
    }
    return String(base).replace(/\/+$/, '');
  }

  function cartCookie() {
    var m = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
    return m ? m[1] : '';
  }

  function mark(value) {
    try {
      if (value) localStorage.setItem(LINK_MARK, value); else localStorage.removeItem(LINK_MARK);
    } catch (e) { /* private mode */ }
  }
  function marked() {
    try { return localStorage.getItem(LINK_MARK) || ''; } catch (e) { return ''; }
  }

  function linkValue(s) {
    var id = String((s.customer && s.customer.id) || '');
    var cached = read(LINK_CACHE);
    if (cached && cached.id === id && cached.value) return Promise.resolve(cached.value);
    var base = apiBase();
    if (!base || !id) return Promise.reject(new Error('no backend'));
    return fetch(base + '/api/session/cart-link', { headers: { Authorization: 'Bearer ' + s.token }, cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (!d || !d.ok || !d.value) throw new Error('no link');
        try { localStorage.setItem(LINK_CACHE, JSON.stringify({ id: id, value: d.value })); } catch (e) { /* ignore */ }
        return d.value;
      });
  }

  function setAttribute(value) {
    var attrs = {};
    attrs[LINK_ATTR] = value;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).then(function (r) { if (!r.ok) throw new Error('cart update ' + r.status); });
  }

  /* Never rejects: failing to link must not get in the way of buying. */
  var linking = null;
  function linkCart() {
    if (linking) return linking;
    var s = session();
    var cart = cartCookie();
    var p;
    if (!s) {
      /* Signed out with our note still on the cart: take it off. */
      p = (marked() && cart) ? setAttribute('').then(function () { mark(''); }) : Promise.resolve();
      try { localStorage.removeItem(LINK_CACHE); } catch (e) { /* ignore */ }
    } else if (!cart) {
      p = Promise.resolve();   /* nothing in the cart yet */
    } else {
      var want = cart + '|' + String(s.customer.id || '');
      p = marked() === want ? Promise.resolve() : linkValue(s).then(setAttribute).then(function () {
        mark((cartCookie() || cart) + '|' + String(s.customer.id || ''));
      });
    }
    linking = p.catch(function () { /* local only; the order still goes through */ })
      .then(function () { linking = null; });
    return linking;
  }

  /* Go to checkout once the cart knows the account, or after a short wait
     at most. */
  function go(dest) {
    var done = false;
    function leave() { if (done) return; done = true; window.location.href = dest; }
    setTimeout(leave, 1800);
    linkCart().then(leave);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', linkCart);
  else linkCart();
  /* A different account signing in re-links; signing out keeps the mark so
     the note is taken off the cart. */
  document.addEventListener('vro:session-changed', function () { if (session()) mark(''); linkCart(); });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') linkCart(); });
  /* Shopify's /cart/add leaves the cart cookie behind; pick it up as soon
     as something has been added, without waiting for the next page. */
  ['cart:updated', 'cart:refresh', 'vro:cart-changed'].forEach(function (name) {
    document.addEventListener(name, function () { linkCart(); });
  });

  /* The cart drawer's Checkout link, and any other plain link to /checkout. */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || a.target === '_blank' || !isCheckoutPath(a.getAttribute('href'))) return;
    if (!session()) return;
    if (magicOwnsCheckout()) return;
    e.preventDefault();
    var u = new URL(a.getAttribute('href'), window.location.href);
    go(url(u.pathname + u.search));
  }, true);

  /* The cart page: its form posts to /cart with a "checkout" button, which
     saves the quantities and note and then goes to checkout — without our
     details. Save them the same way, then go with them. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    var submitter = e.submitter;
    if (e.defaultPrevented || !form || !submitter || submitter.name !== 'checkout') return;
    if (!session()) return;
    if (magicOwnsCheckout()) return;
    e.preventDefault();
    var body = new FormData(form);
    fetch('/cart/update.js', { method: 'POST', body: body, headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('cart update ' + r.status);
        go(url('/checkout'));
      })
      .catch(function () {
        /* The cart's own way, so checkout is never further away than it
           was: the form posted as it would have been. */
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'checkout';
        hidden.value = submitter.value || '';
        form.appendChild(hidden);
        HTMLFormElement.prototype.submit.call(form);
      });
  }, true);

  /* Fetched in the background, well before anyone reaches for Checkout.
     Also on sign-in, since that is the moment there is finally someone to
     ask about — and the tab that signs in is usually the one that goes on
     to buy. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadSavedAddress);
  } else {
    loadSavedAddress();
  }
  document.addEventListener('vro:session-changed', function () {
    /* A different person may be signing in on this tab; last session's
       address must not follow them to checkout. */
    try { sessionStorage.removeItem(ADDRESS_KEY); } catch (e) {}
    loadSavedAddress();
  });

  window.VroCheckout = { url: url, params: params, go: go, link: linkCart, signedIn: function () { return !!session(); } };
})();
